export interface Material {
  name: string;
  type: "3d-print" | "alternative" | "both";
  specs?: string;
  homestyleSource?: string;
  purchaseSource?: string;
  approxPrice?: string;
}

export interface Project {
  id: string;
  title: string;
  discipline: string;
  difficulty: "Fácil" | "Medio" | "Avanzado";
  safetyRating: "Seguro" | "Precaución baja" | "Precaución media" | "Supervisión recomendada" | "Precaución alta";
  timeEstimate: string;
  description: string;
  materials: Material[];
  steps: string[];
  scientificPrinciple: string;
  formula?: {
    expression: string;
    description: string;
  };
  simulationType?: "lever" | "motor" | "solar";
}

export interface Inventor {
  id: string;
  name: string;
  years: string;
  field: string;
  quote: string;
  description: string;
  notebookText: string;
  sketches: string[]; // Mock file paths/descriptions
  replicatedProjects: string[]; // Project IDs
}

export const projects: Project[] = [
  {
    id: "tesla-motor",
    title: "Motor Eléctrico Homopolar (Tesla / Faraday)",
    discipline: "Electricidad",
    difficulty: "Fácil",
    safetyRating: "Seguro",
    timeEstimate: "30 minutos",
    description: "Construye el motor eléctrico más simple del mundo. Utiliza fuerzas magnéticas para hacer girar un conductor usando una batería y imanes de neodimio.",
    materials: [
      { 
        name: "Soporte de Batería Magnético", 
        type: "3d-print", 
        specs: "PLA, 15% infill, sin soportes",
        homestyleSource: "Se debe imprimir en 3D en tu taller/impresora local.",
        purchaseSource: "Servicios de impresión 3D (como Craftcloud, Treatstock o Bambu Lab Hub) si no tienes impresora propia.",
        approxPrice: "$2.00 - $4.00"
      },
      { 
        name: "Imanes de Neodimio circulares", 
        type: "alternative", 
        specs: "2 unidades (12mm x 3mm)",
        homestyleSource: "Desmantela cierres magnéticos de bolsos antiguos, juguetes imantados en desuso o soportes de celular magnéticos para auto.",
        purchaseSource: "Ferreterías locales, tiendas de pasatiempos, ferreterías de electricidad o Amazon.",
        approxPrice: "$3.00 (Pack de 5)"
      },
      { 
        name: "Pila alcalina AA", 
        type: "alternative", 
        specs: "1.5V estándar",
        homestyleSource: "Recupérala de un control remoto de TV, reloj de pared, juguete viejo o linterna de tu casa.",
        purchaseSource: "Supermercados locales, quioscos, tiendas de conveniencia o farmacias.",
        approxPrice: "$1.00"
      },
      { 
        name: "Cable de cobre desnudo", 
        type: "alternative", 
        specs: "Calibre 14 o 16 (aproximadamente 20cm)",
        homestyleSource: "Pela un trozo de cable eléctrico doméstico sobrante o un cargador de corriente viejo de 2 hilos que ya no uses.",
        purchaseSource: "Ferreterías (se vende por metro) o tiendas de electrónica.",
        approxPrice: "$0.50 por metro"
      }
    ],
    steps: [
      "Coloca los imanes de neodimio en el polo negativo (-) de la pila alcalina.",
      "Dobla el cable de cobre para formar una estructura simétrica (por ejemplo, en forma de corazón o espira) que pueda pivotar en el polo positivo (+) superior y rodear los imanes en la parte inferior.",
      "Coloca el cable sobre el polo positivo. El extremo inferior debe tocar suavemente los imanes laterales.",
      "Observa cómo la fuerza de Lorentz hace rotar la espira de cobre a alta velocidad."
    ],
    scientificPrinciple: "Fuerza de Lorentz: Cuando una corriente eléctrica pasa por un conductor inmerso en un campo magnético perpendicular, experimenta una fuerza mecánica que genera rotación.",
    formula: {
      expression: "F = I * L * B * sin(θ)",
      description: "Donde F es la fuerza de Lorentz, I es la corriente eléctrica, L es la longitud del conductor, B es el campo magnético y θ es el ángulo entre la corriente y el campo."
    },
    simulationType: "motor"
  },
  {
    id: "archimedes-lever",
    title: "Palanca de Arquímedes y Multiplicación de Fuerza",
    discipline: "Física",
    difficulty: "Fácil",
    safetyRating: "Seguro",
    timeEstimate: "20 minutos",
    description: "Demuestra la ley de la palanca. Experimenta cómo cambiar el punto de apoyo (fulcro) permite levantar cargas pesadas con un mínimo esfuerzo físico.",
    materials: [
      { 
        name: "Palanca graduada con ranuras", 
        type: "3d-print", 
        specs: "PLA rígido, 25% infill para soporte estructural",
        homestyleSource: "Reemplazable por una regla escolar plana de plástico de 30cm o una espátula de madera.",
        purchaseSource: "Impresión 3D (para la versión ranurada con encajes exactos) o librerías locales.",
        approxPrice: "$1.00 (regla) o $6.00 (3D)"
      },
      { 
        name: "Fulcro (Punto de apoyo triangular)", 
        type: "3d-print", 
        specs: "PLA, 15% infill",
        homestyleSource: "Corta un trozo triangular de madera de desecho, un borrador escolar de forma prismática, o usa un lápiz hexagonal como rodillo de apoyo.",
        purchaseSource: "Impresión 3D o ferreterías (cuñas de madera).",
        approxPrice: "$0.00 (casero) o $2.00 (3D)"
      },
      { 
        name: "Monedas u objetos pesados idénticos", 
        type: "alternative", 
        specs: "Para usar como masas de prueba",
        homestyleSource: "Monedas de tu alcancía, tuercas de acero idénticas del taller, o piedras pequeñas uniformes.",
        purchaseSource: "Ferreterías (tuercas y arandelas por peso).",
        approxPrice: "$0.50 (tuercas)"
      },
      { 
        name: "Regla o varilla de madera de 30cm", 
        type: "alternative", 
        specs: "Alternativa si no tienes impresora 3D",
        homestyleSource: "Regla plástica convencional, varilla de madera para manualidades o percha de ropa desarmada.",
        purchaseSource: "Papelerías, librerías o tiendas de manualidades.",
        approxPrice: "$1.00"
      }
    ],
    steps: [
      "Coloca el fulcro triangular en el centro (marca de 15cm) de la barra o regla.",
      "Coloca un peso de 100g (por ejemplo, 10 monedas) en el extremo izquierdo a 10cm del fulcro.",
      "Coloca un peso de 50g (5 monedas) en el extremo derecho. Ajusta su distancia del fulcro hasta lograr el equilibrio exacto (debe ser a 20cm).",
      "Varía la distancia del fulcro para ver el efecto multiplicador de la distancia (brazo de palanca)."
    ],
    scientificPrinciple: "Momento de torsión (Torque): Para que una palanca esté en equilibrio estático, la suma de los torques en sentido horario debe ser igual a la de sentido antihorario.",
    formula: {
      expression: "F1 * d1 = F2 * d2",
      description: "Donde F1 y F2 son las fuerzas aplicadas en cada extremo, y d1 y d2 son sus respectivas distancias al fulcro."
    },
    simulationType: "lever"
  },
  {
    id: "solar-still",
    title: "Destilador Solar de Supervivencia",
    discipline: "Renovables",
    difficulty: "Medio",
    safetyRating: "Seguro",
    timeEstimate: "1 hora",
    description: "Purifica agua sucia o desaliniza agua de mar usando energía solar. Un gran proyecto para entender el ciclo del agua y el aprovechamiento de la radiación térmica.",
    materials: [
      { 
        name: "Boquilla colectora de condensación", 
        type: "3d-print", 
        specs: "PETG o ABS (más resistente al calor y seguro para alimentos)",
        homestyleSource: "Se puede omitir o reemplazar haciendo un embudo con el cuello cortado de una botella de plástico limpia.",
        purchaseSource: "Impresión 3D (para una boquilla hermética que encaje en el vaso central).",
        approxPrice: "$3.00"
      },
      { 
        name: "Recipiente grande de boca ancha", 
        type: "alternative", 
        specs: "Una ensaladera, bote de plástico o caja de madera sellada",
        homestyleSource: "Un tazón de ensalada de vidrio, un balde de plástico limpio o un contenedor hermético de cocina.",
        purchaseSource: "Supermercados o tiendas de plástico doméstico.",
        approxPrice: "$3.50"
      },
      { 
        name: "Vaso o taza pequeña", 
        type: "alternative", 
        specs: "Se coloca en el centro para recibir el agua limpia",
        homestyleSource: "Taza de cerámica, vaso de vidrio corto para agua o frasco de vidrio pequeño de conserva.",
        purchaseSource: "Tiendas de menaje o supermercados.",
        approxPrice: "$1.00"
      },
      { 
        name: "Plástico transparente (film de cocina)", 
        type: "alternative", 
        specs: "Para cubrir el recipiente",
        homestyleSource: "Film plástico de envolver alimentos (Vinipel, Alusa, stretch wrap) de tu alacena de cocina.",
        purchaseSource: "Supermercados o almacenes de barrio.",
        approxPrice: "$2.00 por rollo"
      },
      { 
        name: "Piedra pequeña o peso", 
        type: "alternative", 
        specs: "Para hacer peso en el centro del plástico",
        homestyleSource: "Una piedra redondeada de tu jardín, patio, o una tuerca pesada.",
        purchaseSource: "Gratis (recolectado en la naturaleza).",
        approxPrice: "$0.00"
      }
    ],
    steps: [
      "Vierte agua salada o sucia en el fondo del recipiente grande sin que llegue a entrar en el vaso central.",
      "Coloca el vaso colector vacío exactamente en el centro del recipiente.",
      "Cubre todo el recipiente con el film plástico transparente, asegurando los bordes con cinta o una gama para que quede hermético.",
      "Coloca la piedrecilla en el centro del plástico, justo encima del vaso colector, creando una pendiente en forma de embudo invertido.",
      "Coloca el destilador al sol directo. El agua se evaporará, se condensará en el plástico, goteará hacia el centro debido a la inclinación y caerá pura en el vaso."
    ],
    scientificPrinciple: "Termodinámica y Cambio de Fase: La radiación solar calienta el agua líquida provocando su evaporación. Al chocar contra el plástico (más frío), el vapor se condensa volviendo a estado líquido, libre de sales y contaminantes.",
    formula: {
      expression: "Q = m * L_v",
      description: "Donde Q es el calor necesario para evaporar la masa de agua 'm', y L_v es el calor latente de vaporización del agua."
    },
    simulationType: "solar"
  },
  {
    id: "davinci-bridge",
      title: "Puente Autoportante (Leonardo da Vinci)",
      discipline: "Construcción / Arquitectura",
      difficulty: "Medio",
      safetyRating: "Seguro",
      timeEstimate: "45 minutos",
      description: "Construye un puente autoportante de arco usando listones de madera entrelazados. Se sostiene enteramente por gravedad y fricción sin necesidad de clavos o pegamento.",
      materials: [
        {
          name: "Listones de madera ranurados (15 unidades)",
          type: "3d-print",
          specs: "PLA, 20% infill para resistencia estructural",
          homestyleSource: "Se puede imprimir en 3D en tu taller/impresora local.",
          purchaseSource: "Servicios de impresión 3D (Craftcloud o similar)."
        },
        {
          name: "Listones alternativos de madera plana",
          type: "alternative",
          specs: "15 listones idénticos de 15cm x 1cm",
          homestyleSource: "Usa depresores de lengua de madera o palitos de helado.",
          purchaseSource: "Tiendas de manualidades o farmacias locales."
        }
      ],
      steps: [
        "Coloca dos listones base en paralelo sobre el suelo.",
        "Cruza un listón transversal sobre ellos en la parte superior.",
        "Entrelaza dos nuevos listones longitudinales apoyándolos en el transversal y pasando por debajo del siguiente conjunto.",
        "Repite el patrón de entrelazado alterno hasta formar un arco autoportante semicircular.",
        "Aplica un peso ligero en el centro del arco para comprobar cómo la fricción aumenta y traba las uniones en su sitio."
      ],
      scientificPrinciple: "Distribución de Carga y Fricción Estática: La carga vertical sobre el arco genera fuerzas de compresión normales en las uniones de madera, incrementando la fuerza de fricción estática y bloqueando la estructura.",
      formula: {
        expression: "F_f <= μ * N",
        description: "Donde F_f es la fuerza de fricción estática, μ es el coeficiente de fricción de la madera y N es la fuerza normal en la junta."
      },
      simulationType: "lever"
    },
    {
      id: "dna-extraction",
      title: "Extracción Casera de ADN (Rosalind Franklin)",
      discipline: "Biología",
      difficulty: "Fácil",
      safetyRating: "Supervisión recomendada",
      timeEstimate: "30 minutos",
      description: "Aísla y observa a simple vista las hebras de material genético (ADN) de fresas o plátanos utilizando reactivos comunes del hogar.",
      materials: [
        {
          name: "Tubos de ensayo graduados",
          type: "3d-print",
          specs: "PETG transparente, modo vaso (1 contorno)",
          homestyleSource: "Se puede imprimir en 3D.",
          purchaseSource: "Servicios de impresión 3D."
        },
        {
          name: "Fresas o medio plátano",
          type: "alternative",
          specs: "Como fuente de material celular",
          homestyleSource: "Fresas o plátanos maduros de tu cocina.",
          purchaseSource: "Supermercados o fruterías locales."
        },
        {
          name: "Detergente de platos líquido",
          type: "alternative",
          specs: "Para preparar la solución de lisis celular",
          homestyleSource: "Detergente lavaplatos regular.",
          purchaseSource: "Supermercados locales."
        },
        {
          name: "Alcohol isopropílico frío (70%+)",
          type: "alternative",
          specs: "100ml (mantener en el congelador)",
          homestyleSource: "Alcohol de botiquín del hogar.",
          purchaseSource: "Farmacias locales en Puerto Rico."
        }
      ],
      steps: [
        "Tritura completamente la fruta en una bolsa plástica sellada para romper los tejidos mecánicamente.",
        "Prepara la solución de lisis mezclando 90ml de agua con sal y 10ml de detergente de platos.",
        "Añade 2 cucharadas de la solución de lisis a la fruta triturada y mezcla suavemente por 5 minutos.",
        "Filtra la mezcla a través de un colador o filtro de café en un vaso limpio para retirar los sólidos.",
        "Ladea el vaso y vierte lentamente alcohol isopropílico frío por la pared para formar una capa flotante.",
        "Observa las hebras blancas y viscosas de ADN precipitar en la capa de alcohol."
      ],
      scientificPrinciple: "Lisis celular y Precipitación: El detergente disuelve los lípidos de las membranas celulares y nucleares liberando el ADN. El alcohol disminuye la constante dieléctrica y hace que las moléculas de ADN precipiten.",
      formula: {
        expression: "C1 * V1 = C2 * V2",
        description: "Ecuación de dilución para ajustar la concentración del agente tensoactivo en la solución de lisis."
      },
      simulationType: "solar"
    },
    {
      id: "water-electrolysis",
      title: "Celda de Electrólisis del Agua (Antoine Lavoisier)",
      discipline: "Química",
      difficulty: "Medio",
      safetyRating: "Precaución alta",
      timeEstimate: "40 minutos",
      description: "Descompón el agua molecular (H2O) en gas Hidrógeno (H2) y gas Oxígeno (O2) aplicando corriente eléctrica continua.",
      materials: [
        {
          name: "Soporte de Electrodos de Grafito",
          type: "3d-print",
          specs: "PLA, 15% infill",
          homestyleSource: "Se puede imprimir en 3D en tu taller.",
          purchaseSource: "Servicio de impresión local."
        },
        {
          name: "Pila de 9V o fuente de 12V CC",
          type: "alternative",
          specs: "Suministro de energía eléctrica continua",
          homestyleSource: "Pila de 9V de un detector de humo de repuesto.",
          purchaseSource: "Supermercados o farmacias."
        },
        {
          name: "Lápices de madera de grafito (2 unidades)",
          type: "alternative",
          specs: "Sin las gomas, pelados por ambos extremos para usar de electrodos",
          homestyleSource: "Lápices escolares de tu cajón.",
          purchaseSource: "Papelerías o supermercados."
        },
        {
          name: "Bicarbonato de Sodio (NaHCO₃)",
          type: "alternative",
          specs: "Electrolito seguro para generar H₂ y O₂ sin vapores de cloro",
          homestyleSource: "Bicarbonato de cocina o repostería.",
          purchaseSource: "Supermercados o farmacias de PR."
        }
      ],
      steps: [
        "Llena el vaso de vidrio con agua y disuelve el bicarbonato de sodio revolviendo bien.",
        "Coloca el soporte de electrodos impreso en 3D en la boca del vaso.",
        "Inserta los dos lápices de grafito a través del soporte de manera que las puntas queden sumergidas pero separadas.",
        "Conecta un extremo de un cable al polo positivo de la pila y al grafito superior de un lápiz (ánodo).",
        "Conecta el polo negativo al grafito superior del otro lápiz (cátodo).",
        "Observa la ebullición de burbujas en ambos electrodos: Hidrógeno en el cátodo (doble volumen) y Oxígeno en el ánodo."
      ],
      scientificPrinciple: "Reacción Redox Electroquímica: La diferencia de potencial eléctrico fuerza a los electrones a reducir las moléculas de agua en el cátodo y oxidarlas en el ánodo.",
      formula: {
        expression: "2 H2O (l) -> 2 H2 (g) + O2 (g)",
        description: "Reacción química balanceada de descomposición electroquímica del agua."
      },
      simulationType: "motor"
    },
    {
      id: "astrolabe-stars",
      title: "Astrolabio Estelar y Visor de Altitud (Galileo)",
      discipline: "Astronomía / Filosofía",
      difficulty: "Medio",
      safetyRating: "Seguro",
      timeEstimate: "1 hora",
      description: "Construye un astrolabio clásico para medir la altura angular de las estrellas y calcular la latitud geográfica en Puerto Rico.",
      materials: [
        {
          name: "Placa del Astrolabio graduada",
          type: "3d-print",
          specs: "PLA, cara superior lisa y grabada",
          homestyleSource: "Imprime la plantilla en cartulina o cartón rígido.",
          purchaseSource: "Servicios de impresión 3D."
        },
        {
          name: "Alidada (Visor móvil central)",
          type: "3d-print",
          specs: "PLA, encaje en eje central",
          homestyleSource: "Reemplazable por un sorbete pegado a un transportador de ángulos.",
          purchaseSource: "Papelerías locales."
        }
      ],
      steps: [
        "Ensambla el visor de alidada sobre la placa astrolabio graduada en el eje central usando el tornillo.",
        "Ata la cuerda con la tuerca pequeña al visor central para que cuelgue libremente como plomada.",
        "Apunta el visor a una estrella de referencia (por ejemplo, Polaris) mirando a través de las rendijas de la alidada.",
        "Lee el ángulo de elevación indicado por la cuerda de la plomada en la escala graduada.",
        "Aplica la fórmula matemática astronómica para de este modo estimar tu latitud geográfica local en Puerto Rico."
      ],
      scientificPrinciple: "Proyección angular y Geometría Celeste: La elevación angular del polo celeste norte (estrella Polar) sobre el horizonte es geométricamente igual a la latitud del observador en la Tierra.",
      formula: {
        expression: "Latitud = Elevación_Polar",
        description: "Donde la elevación angular medida de la estrella Polaris indica directamente la latitud de tu ubicación."
      },
      simulationType: "lever"
    },
    {
      id: "voltaic-pile",
      title: "Pila de Volta Casera (Alessandro Volta)",
      discipline: "Química / Electricidad",
      difficulty: "Fácil",
      safetyRating: "Seguro",
      timeEstimate: "30 minutos",
      description: "Construye una batería química galvánica apilando monedas de cobre y láminas de aluminio separadas por cartón mojado en vinagre. Aprende cómo el flujo de electrones genera electricidad útil en emergencias.",
      materials: [
        {
          name: "Monedas de Cobre (Ej: chavitos de Puerto Rico)",
          type: "alternative",
          specs: "Límpialas con sal y vinagre para máxima conductividad",
          homestyleSource: "Monedas domésticas de tu alcancía.",
          purchaseSource: "Ferreterías Ace / National Lumber (puedes usar arandelas de cobre si no tienes monedas).",
          approxPrice: "$0.50"
        },
        {
          name: "Láminas de Aluminio o Zinc",
          type: "alternative",
          specs: "Papel de aluminio grueso o arandelas de zinc galvanizadas",
          homestyleSource: "Papel de aluminio de cocina estándar.",
          purchaseSource: "Supermercados o ferreterías locales.",
          approxPrice: "$1.00"
        },
        {
          name: "Vinagre Blanco o Agua Salada",
          type: "alternative",
          specs: "Electrolito ácido débil",
          homestyleSource: "Alacena de la cocina.",
          purchaseSource: "Supermercado Econo / Selectos.",
          approxPrice: "$1.50"
        },
        {
          name: "Diodo LED (Pequeño)",
          type: "both",
          specs: "LED de bajo consumo 2V, color rojo o verde",
          homestyleSource: "Recicla el foco indicador de un cargador o juguete roto.",
          purchaseSource: "Envío USPS de Amazon / eBay.",
          approxPrice: "$0.80"
        }
      ],
      steps: [
        "Limpia las monedas de cobre con una mezcla de sal y vinagre para remover el óxido superficial.",
        "Corta círculos de papel de aluminio y de cartón que coincidan con el diámetro de las monedas.",
        "Sumerge los discos de cartón en un vaso con vinagre blanco durante 5 minutos para empaparlos con electrolito.",
        "Comienza a apilar en este orden exacto: Moneda de cobre, cartón húmedo, lámina de aluminio. Repite este ciclo de 6 a 10 veces.",
        "Conecta las patas del LED (pata larga es ánodo positivo, pata corta es cátodo negativo) a los extremos superior e inferior de la pila para encender la luz."
      ],
      scientificPrinciple: "Electroquímica y Reacciones Redox: La diferencia de electronegatividad entre el cobre y el aluminio impulsa una transferencia de electrones a través del electrolito de vinagre ácido, estableciendo una corriente eléctrica.",
      formula: {
        expression: "V_{total} = N \\times V_{celda}",
        description: "Donde N es el número de celdas apiladas y V_celda es el potencial galvánico (aprox. 0.6V a 0.9V por celda)."
      },
      simulationType: "lever"
    },
    {
      id: "zeer-fridge",
      title: "Vasija de Zeer - Nevera del Desierto (Leyes Psicrométricas)",
      discipline: "Termodinámica / Fluidos",
      difficulty: "Medio",
      safetyRating: "Seguro",
      timeEstimate: "45 minutos",
      description: "Crea una cámara enfriadora no eléctrica que utiliza dos macetas de barro concéntricas y arena húmeda. Enfría el compartimiento interior mediante enfriamiento evaporativo pasivo, ideal para conservar alimentos bajo el sol de Puerto Rico.",
      materials: [
        {
          name: "Maceta de barro grande (con agujeros tapados)",
          type: "alternative",
          specs: "Diámetro de 10-12 pulgadas sin esmaltar (porosa)",
          homestyleSource: "Macetas de plantas vacías en tu patio.",
          purchaseSource: "Ferretería Ace / Home Depot (PR).",
          approxPrice: "$6.00"
        },
        {
          name: "Maceta de barro pequeña",
          type: "alternative",
          specs: "Diámetro de 6-8 pulgadas sin esmaltar",
          homestyleSource: "Buscar en el jardín.",
          purchaseSource: "Ferreterías locales de comunidad.",
          approxPrice: "$3.50"
        },
        {
          name: "Arena fina",
          type: "alternative",
          specs: "Arena de construcción o arena limpia de playa",
          homestyleSource: "Se puede recolectar gratis en la costa o patio.",
          purchaseSource: "National Lumber & Hardware.",
          approxPrice: "$2.00"
        },
        {
          name: "Paño de tela húmedo",
          type: "alternative",
          specs: "Tela de arpillera o algodón absorbente",
          homestyleSource: "Camiseta vieja cortada o paño de cocina.",
          purchaseSource: "Supermercados locales.",
          approxPrice: "$0.50"
        }
      ],
      steps: [
        "Tapa los agujeros de drenaje de ambas macetas de barro usando corcho o silicona para evitar fugas de arena.",
        "Coloca una capa de arena de 2 pulgadas en el fondo de la maceta grande.",
        "Coloca la maceta pequeña en el centro de la grande, apoyada sobre la arena del fondo.",
        "Llena el espacio concéntrico entre ambas macetas con arena fina hasta el tope.",
        "Vierte agua fría sobre la arena hasta que esté completamente saturada (sin flotar la maceta interior).",
        "Introduce los alimentos en la maceta interna y cúbrela con el paño de tela húmedo. Coloca la vasija en un área ventilada."
      ],
      scientificPrinciple: "Calor Latente de Vaporización: La evaporación del agua de la arena húmeda a través del barro poroso absorbe energía térmica del interior de la maceta pequeña, disminuyendo su temperatura interna por debajo de la ambiental.",
      formula: {
        expression: "T_{int} \\approx T_{hum} = f(T_{amb}, HR)",
        description: "Donde T_int se aproxima a la temperatura de bulbo húmedo T_hum, influenciada por la temperatura ambiental y la humedad relativa."
      },
      simulationType: "solar"
    },
    {
      id: "solar-thermosiphon",
      title: "Calentador Solar por Termosifón (Convección de Fluidos)",
      discipline: "Termodinámica / Fluidos",
      difficulty: "Avanzado",
      safetyRating: "Supervisión recomendada",
      timeEstimate: "3 horas",
      description: "Construye un colector de agua caliente solar usando tubos de CPVC pintados de negro, botellas plásticas PET recicladas y un envase aislado como tanque acumulador. Aprende cómo el calor del sol mueve el agua sin bombas eléctricas.",
      materials: [
        {
          name: "Tubos de CPVC de 1/2 pulgada",
          type: "alternative",
          specs: "CPVC para agua caliente, conexiones T y codos",
          homestyleSource: "Tramos sobrantes de tuberías en remodelación.",
          purchaseSource: "National Lumber / Home Depot.",
          approxPrice: "$12.00"
        },
        {
          name: "Botellas plásticas PET transparentes (2L)",
          type: "alternative",
          specs: "Botellas vacías de refresco limpias",
          homestyleSource: "Contenedor de reciclaje de tu hogar o vecinos.",
          purchaseSource: "Recolección libre en comunidad.",
          approxPrice: "$0.00"
        },
        {
          name: "Pintura negra mate en aerosol",
          type: "alternative",
          specs: "Pintura resistente al calor y rayos UV",
          homestyleSource: "Sobrantes de pintura de rejas.",
          purchaseSource: "Ferreterías Ace.",
          approxPrice: "$4.50"
        },
        {
          name: "Envase de plástico aislado (Tanque)",
          type: "alternative",
          specs: "Nevera de playa plástica vieja o cubo aislado de 5 galones",
          homestyleSource: "Nevera portátil vieja que no uses en casa.",
          purchaseSource: "Tiendas por departamento locales en PR.",
          approxPrice: "$15.00"
        }
      ],
      steps: [
        "Ensambla una rejilla de tubos de CPVC en paralelo conectados en los extremos con uniones en T y codos.",
        "Corta la base de las botellas plásticas PET e insértalas en los tubos para crear un efecto invernadero alrededor de ellos.",
        "Pinta toda la rejilla de tubos de CPVC de color negro mate para maximizar la absorción térmica.",
        "Conecta la salida superior de la rejilla al tope del tanque térmico aislado, y la entrada inferior a la base del tanque.",
        "Llena el circuito con agua y orienta el colector inclinado 18 grados al sur bajo el sol directo de Puerto Rico."
      ],
      scientificPrinciple: "Convección por Termosifón: Al calentarse el agua en el colector solar negro, disminuye su densidad y asciende de forma natural hacia el tanque superior, siendo reemplazada por agua fría más densa del fondo, estableciendo un flujo continuo sin bombas.",
      formula: {
        expression: "Q = A \\times I \\times \\eta",
        description: "Donde Q es el calor ganado, A es el área del colector, I es la radiación solar y eta es la eficiencia del termosifón."
      },
      simulationType: "solar"
    },
    {
      id: "pascal-press",
      title: "Multiplicador de Presión Hidráulica (Blaise Pascal)",
      discipline: "Ingeniería / Fluidos",
      difficulty: "Medio",
      safetyRating: "Seguro",
      timeEstimate: "1 hora",
      description: "Construye una prensa hidráulica a escala utilizando jeringas plásticas de diferentes diámetros conectadas por una manguera de acuario. Aprende cómo los fluidos incompresibles multiplican fuerzas mecánicas de forma asombrosa.",
      materials: [
        {
          name: "Jeringas plásticas grandes (Pistón de salida)",
          type: "alternative",
          specs: "Capacidad de 50ml o 60ml, sin aguja",
          homestyleSource: "Farmacia local o botiquín doméstico.",
          purchaseSource: "Walgreens / Farmacias de comunidad en PR.",
          approxPrice: "$2.00"
        },
        {
          name: "Jeringas plásticas pequeñas (Pistón de entrada)",
          type: "alternative",
          specs: "Capacidad de 5ml o 10ml",
          homestyleSource: "Jeringas dosificadoras de jarabe de niños.",
          purchaseSource: "Farmacia local.",
          approxPrice: "$1.00"
        },
        {
          name: "Manguera plástica flexible de acuario",
          type: "alternative",
          specs: "Manguera transparente de 4mm de diámetro, 30cm",
          homestyleSource: "Bomba de pecera en desuso.",
          purchaseSource: "Ferreterías Ace / Tiendas de mascotas.",
          approxPrice: "$1.50"
        },
        {
          name: "Soportes de madera o cartón",
          type: "alternative",
          specs: "Listones pequeños para la estructura fija de soporte",
          homestyleSource: "Palitos de madera o cartón grueso de embalaje.",
          purchaseSource: "National Lumber.",
          approxPrice: "$1.00"
        }
      ],
      steps: [
        "Construye un marco rígido en forma de H invertida con los listones de madera para sostener las jeringas de forma vertical.",
        "Toma la manguera flexible y conéctala firmemente a la boquilla de ambas jeringas.",
        "Llena el circuito de manguera y ambas jeringas con agua coloreada, asegurándose de purgar todas las burbujas de aire.",
        "Fija la jeringa grande (pistón de salida) y la pequeña (pistón de entrada) al marco de madera.",
        "Aplica una fuerza presionando el émbolo de la jeringa pequeña y observa cómo la jeringa grande es capaz de levantar pesos mucho mayores."
      ],
      scientificPrinciple: "Principio de Pascal: La presión aplicada a un fluido incompresible y confinado se transmite íntegramente a todas las direcciones del fluido. Al tener un pistón de salida con mayor área, la fuerza aplicada se multiplica proporcionalmente.",
      formula: {
        expression: "F_2 = F_1 \\times \\frac{A_2}{A_1}",
        description: "Donde F2 es la fuerza amplificada, F1 es la fuerza de entrada, y A2/A1 es la relación de áreas de los pisones."
      },
      simulationType: "lever"
    },
    {
      id: "archimedes-buoyancy",
      title: "Vasija de Arquímedes (Principio de Flotación)",
      discipline: "Física / Ciencias",
      difficulty: "Medio",
      safetyRating: "Seguro",
      timeEstimate: "45 minutos",
      description: "Experimenta con la fuerza de empuje sumergiendo objetos de diferente volumen y masa en varios fluidos. Comprueba matemáticamente el principio de Arquímedes y calcula el peso aparente del cuerpo sumergido.",
      materials: [
        {
          name: "Vaso graduado o Probeta graduada",
          type: "alternative",
          specs: "Contenedor transparente de 500ml con marcas de volumen",
          homestyleSource: "Taza medidora de repostería de cocina.",
          purchaseSource: "Ferreterías Ace / Home Depot.",
          approxPrice: "$3.50"
        },
        {
          name: "Bloques de prueba (Madera, metal, plástico)",
          type: "alternative",
          specs: "Varios bloques de igual volumen y diferente masa",
          homestyleSource: "Trozos de madera sobrante, llaves, monedas de chavitos unidas.",
          purchaseSource: "National Lumber.",
          approxPrice: "$1.50"
        },
        {
          name: "Balanza de cocina digital",
          type: "alternative",
          specs: "Precisión de 1g para pesar objetos en seco",
          homestyleSource: "Balanza de pesar alimentos domésticos.",
          purchaseSource: "Supermercados o ferreterías locales.",
          approxPrice: "$9.00"
        }
      ],
      steps: [
        "Pesa el objeto en seco usando la balanza digital para conocer su masa real en gramos.",
        "Llena el vaso medidor graduado con agua hasta una marca de referencia (ej. 300 ml) y anota el nivel.",
        "Sumerge el objeto completamente en el agua y observa el desplazamiento del nivel de fluido.",
        "Mide la nueva marca de volumen. La diferencia de marcas en ml equivale exactamente al volumen del cuerpo en cm³.",
        "Calcula la fuerza de flotación (empuje) multiplicando el volumen desplazado por la densidad del agua y comprueba si flota o se hunde."
      ],
      scientificPrinciple: "Principio de Arquímedes: Todo cuerpo sumergido total o parcialmente en un fluido experimenta un empuje vertical hacia arriba igual al peso del volumen de fluido desalojado.",
      formula: {
        expression: "E = \\rho \\times V \\times g",
        description: "Donde E es el empuje (N), rho es la densidad del fluido (kg/m³), V es el volumen desplazado (m³), y g es la aceleración de gravedad (9.81 m/s²)."
      },
      simulationType: "lever"
    },
    {
      id: "emergency-solar-kit",
      title: "Generador Solar Portátil de Emergencia",
      discipline: "Electricidad / Energías Renovables",
      difficulty: "Avanzado",
      safetyRating: "Precaución media",
      timeEstimate: "4 horas",
      description: "Construye una estación de energía solar portátil autónoma dentro de una caja protectora. Aprende a cablear paneles solares, controlador de carga, batería de ciclo profundo e inversor para tener luz y nevera en apagones de Puerto Rico.",
      materials: [
        {
          name: "Placa Solar de 100W a 150W Monocristalina",
          type: "both",
          specs: "Panel fotovoltaico portátil de 12V con terminales MC4",
          homestyleSource: "Buscar en ferreterías especializadas locales.",
          purchaseSource: "Home Depot / Distribuidores de energía solar en PR.",
          approxPrice: "$85.00"
        },
        {
          name: "Controlador de Carga PWM o MPPT (20A)",
          type: "both",
          specs: "Controlador a 12V/24V para proteger la batería de sobrecargas",
          homestyleSource: "No reciclable, requiere compra especializada.",
          purchaseSource: "Ferreterías Ace / Envío USPS rápido.",
          approxPrice: "$18.00"
        },
        {
          name: "Batería de ciclo profundo (Gel o AGM) o Litio (12V 50Ah)",
          type: "both",
          specs: "Batería sellada adecuada para almacenar energía solar",
          homestyleSource: "Batería de carro vieja (no recomendada pero útil en emergencias extremas).",
          purchaseSource: "National Lumber / Tiendas de auto parts locales.",
          approxPrice: "$95.00"
        },
        {
          name: "Inversor de Corriente CC a CA (500W)",
          type: "both",
          specs: "Inversor de 12V CC a 120V CA onda sinusoidal modificada o pura",
          homestyleSource: "Inversor de carro para cargar laptops.",
          purchaseSource: "Home Depot / Pep Boys.",
          approxPrice: "$35.00"
        }
      ],
      steps: [
        "Monta el controlador de carga, los fusibles y el inversor dentro de una caja de herramientas plástica resistente (caja de carga).",
        "Conecta primero los bornes de la batería de 12V a la entrada de batería del controlador de carga usando cable calibre 10 AWG (¡El controlador debe encender!).",
        "Conecta la salida de cables MC4 del panel solar a la entrada solar del controlador de carga.",
        "Conecta el inversor de corriente directamente a los bornes de la batería (no al controlador) usando terminales de ojo gruesos y fusible de protección.",
        "Coloca el panel solar bajo el sol directo de Puerto Rico y enciende el inversor para conectar abanicos, bombillas LED o cargar equipos médicos de emergencia.",
        "Toda instalación eléctrica en Puerto Rico expuesta a la intemperie debe protegerse contra corrosión salina ('salitre') usando conectores estancos y sellado de silicona marina."
      ],
      scientificPrinciple: "Efecto Fotovoltaico y Almacenamiento Electroquímico: Las celdas de silicio convierten los fotones solares en corriente eléctrica de CC, que el controlador regula para cargar químicamente la batería. El inversor transforma la CC de 12V en CA de 120V de alta frecuencia para uso doméstico.",
      formula: {
        expression: "Capacidad_{Wh} = V_{bat} \\times Ah_{bat} \\times DoD",
        description: "Donde la capacidad útil en Wh depende del voltaje de batería, los Ah de capacidad y el porcentaje de descarga recomendado (DoD, ej. 50% en plomo ácido, 80% en litio)."
      },
      simulationType: "solar"
    },
    {
      id: "diy-hologram-pyramid",
      title: "Visor Holográfico 3D Piramidal Casero",
      discipline: "Física / Óptica",
      difficulty: "Fácil",
      safetyRating: "Precaución baja",
      timeEstimate: "30 minutos",
      description: "Construye un reflector óptico en forma de pirámide invertida usando plástico transparente rígido para transformar videos de 4 cuadrantes de tu celular en hologramas flotantes 3D en el centro del visor.",
      materials: [
        {
          name: "Lámina plástica transparente rígida",
          type: "alternative",
          specs: "Plástico transparente cortado de empaques o caja de CD vieja",
          homestyleSource: "Cubierta plástica de cajas de juguetes, estuches de CD o botellas lisas de refresco.",
          purchaseSource: "Supermercados o ferreterías locales.",
          approxPrice: "$0.50"
        },
        {
          name: "Cinta adhesiva transparente o Silicona caliente",
          type: "alternative",
          specs: "Adhesivo para unir los bordes plásticos",
          homestyleSource: "Tape escolar transparente de oficina.",
          purchaseSource: "Ferretería Ace / Farmacias locales.",
          approxPrice: "$1.50"
        },
        {
          name: "Papel cuadriculado y bolígrafo",
          type: "alternative",
          specs: "Para trazar la plantilla trapezoide exacta",
          homestyleSource: "Libreta vieja de matemáticas.",
          purchaseSource: "Libre en tu hogar.",
          approxPrice: "$0.00"
        }
      ],
      steps: [
        "Dibuja en papel un trapecio con las siguientes medidas estándar: Base mayor = 6 cm, Base menor = 1 cm, Altura = 3.5 cm.",
        "Usa esa plantilla para recortar 4 trapecios idénticos en el plástico transparente rígido con mucho cuidado.",
        "Une los 4 trapecios por los bordes laterales usando pequeños trozos de cinta adhesiva transparente hasta formar una pirámide de cuatro caras.",
        "Coloca la pirámide plástica invertida (base menor de 1 cm apuntando hacia abajo) justo en el centro geométrico de tu celular.",
        "Reproduce un video holográfico de 4 caras en la pantalla, apaga las luces de tu habitación y observa el objeto 3D flotando mágicamente en el aire."
      ],
      scientificPrinciple: "Reflexión y Refracción de la Luz: Las caras de plástico transparente inclinadas a 45 grados reflejan la luz proveniente de la pantalla del celular hacia la línea de visión del observador, superponiendo la imagen reflejada en el espacio vacío del centro de la pirámide.",
      formula: {
        expression: "\\theta_i = \\theta_r",
        description: "Ley de Reflexión: El ángulo de incidencia de los rayos de luz de la pantalla es igual al ángulo de reflexión de la cara plástica (45 grados), alineando la imagen con el eje vertical."
      },
      simulationType: "solar"
    }
  ];

export const inventors: Inventor[] = [
  {
    id: "da-vinci",
    name: "Leonardo da Vinci",
    years: "1452 — 1519",
    field: "Polimatía e Ingeniería",
    quote: "El estudio sin deseo daña la memoria, y no retiene nada de lo que toma.",
    description: "Pintor, escultor, arquitecto, científico y escritor, Da Vinci es considerado el arquetipo del hombre del Renacimiento. Sus cuadernos contienen cientos de bocetos de máquinas voladoras, puentes autoportantes y engranajes mecánicos adelantados a su época.",
    notebookText: "Mis observaciones sobre la palanca mecánica demuestran que las fuerzas actúan en proporción inversa a las distancias al fulcro. He diseñado grúas y catapultas basándome en este equilibrio geométrico, el cual rige también los tendones del brazo humano.",
    sketches: ["Boceto de palancas de grúa", "Músculo del hombro como polea"],
    replicatedProjects: ["archimedes-lever", "davinci-bridge"]
  },
  {
    id: "tesla",
    name: "Nikola Tesla",
    years: "1856 — 1943",
    field: "Electromagnetismo y Frecuencia",
    quote: "Si quieres descubrir los secretos del universo, piensa en términos de energía, frecuencia y vibración.",
    description: "Inventor serbocroata nacionalizado estadounidense, célebre por sus patentes de corriente alterna, motores de inducción y transmisiones de radio. Su genialidad residía en su capacidad para visualizar y probar mentalmente el funcionamiento de sus máquinas antes de construirlas.",
    notebookText: "El magnetismo giratorio es la danza de las fuerzas invisibles. Al inducir corriente eléctrica en una espira colocada bajo un campo polar alternante, la fuerza de Lorentz se manifiesta, convirtiendo el calor latente del flujo magnético en torque mecánico continuo.",
    sketches: ["Rotor de campo magnético giratorio", "Bobina resonante de alta frecuencia"],
    replicatedProjects: ["tesla-motor"]
  },
  {
    id: "curie",
    name: "Marie Curie",
    years: "1867 — 1934",
    field: "Física y Química",
    quote: "Nada en la vida debe ser temido, solo comprendido.",
    description: "Científica polaca-francesa pionera en el campo de la radioactividad. Primera persona en recibir dos premios Nobel en diferentes especialidades (Física y Química) y la primera mujer en dar clases en la Universidad de París.",
    notebookText: "La destilación y el fraccionamiento de compuestos son rituales de paciencia. Para purificar el mineral, medimos la energía latente de evaporación y calor liberado. Cada gota de destilado puro es una victoria de la precisión contra las impurezas del suelo.",
    sketches: ["Aparato de cristal para electrólisis", "Esquema de radiación de pechblenda"],
    replicatedProjects: ["solar-still", "water-electrolysis"]
  },
  {
    id: "einstein",
    name: "Albert Einstein",
    years: "1879 — 1955",
    field: "Física Teórica",
    quote: "La imaginación es más importante que el conocimiento.",
    description: "Físico alemán nacionalizado estadounidense, creador de las teorías de la relatividad general y especial, y explicador del efecto fotoeléctrico. Su trabajo redefinió por completo nuestra comprensión de la gravedad, el tiempo y el espacio tridimensional.",
    notebookText: "El espacio y el tiempo no son absolutos; se curvan bajo la influencia de la materia y la energía. Un fotón de luz solar arranca electrones de un metal del destilador o panel solar debido a los paquetes discretos de energía (cuantos).",
    sketches: ["Representación del tejido espacio-temporal", "Esquema de emisión fotoeléctrica"],
    replicatedProjects: ["solar-still"]
  },
  {
    id: "pasteur",
    name: "Louis Pasteur",
    years: "1822 — 1895",
    field: "Microbiología y Química",
    quote: "La fortuna favorece a la mente preparada.",
    description: "Químico y físico francés, fundador de la microbiología moderna. Descubridor de la pasteurización y creador de vacunas clave contra la rabia. Desmitificó la generación espontánea y abogó por la asepsia médica.",
    notebookText: "Los microbios se multiplican rápidamente en la humedad tropical. Para mantener un ambiente estéril en nuestro taller Prometheus, es clave hervir y sellar los recipientes. Todo instrumento de laboratorio debe tratarse con calor y agentes antisépticos.",
    sketches: ["Matraz con cuello de cisne", "Estudio microscópico de levaduras"],
    replicatedProjects: ["solar-still", "dna-extraction"]
  },
  {
    id: "lavoisier",
    name: "Antoine Lavoisier",
    years: "1743 — 1794",
    field: "Química Moderna",
    quote: "La materia no se crea ni se destruye, solo se transforma.",
    description: "Químico francés, considerado el padre de la química moderna por sus estudios sobre la combustión, la ley de conservación de la masa y la nomenclatura química cuantitativa.",
    notebookText: "Las masas de nuestros reactivos deben sumarse exactamente igual antes y después de cada reacción. El agua destilada mediante calor solar representa una evaporación física limpia, donde la suma del destilado y residuo equivale al volumen de partida.",
    sketches: ["Balanza de precisión de laboratorio", "Aparato para la combustión del oxígeno"],
    replicatedProjects: ["solar-still", "water-electrolysis"]
  },
  {
    id: "franklin",
    name: "Rosalind Franklin",
    years: "1920 — 1958",
    field: "Biología Molecular y Cristalografía",
    quote: "La ciencia y la vida cotidiana no pueden ni deben separarse.",
    description: "Biofísica y cristalógrafa británica cuyo trabajo con difracción de rayos X (incluyendo la célebre Fotografía 51) fue fundamental para descubrir la estructura tridimensional de doble hélice del ADN.",
    notebookText: "El patrón de difracción en forma de cruz revela la naturaleza helicoidal perfecta del ácido desoxirribonucleico. La geometría molecular es la clave de toda arquitectura biológica, uniendo la física estructural con los secretos de la herencia.",
    sketches: ["Fotografía 51 (Patrón de difracción de rayos X)", "Esquema helicoidal de doble cadena"],
    replicatedProjects: ["dna-extraction"]
  },
  {
    id: "galileo",
    name: "Galileo Galilei",
    years: "1564 — 1642",
    field: "Astronomía y Física Experimental",
    quote: "Y sin embargo, se mueve (Eppur si muove).",
    description: "Físico, astrónomo e ingeniero italiano pionero del método científico. Mejoró el telescopio reflector para observar los satélites de Júpiter y formuló las leyes de la caída libre y cinemática clásica.",
    notebookText: "Las esferas de metal ruedan por planos inclinados acelerando en proporción geométrica al tiempo transcurrido. La observación directa del movimiento, sin dogmas antiguos, abre la puerta para diseñar engranajes y péndulos de alta precisión.",
    sketches: ["Boceto de planos inclinados y esferas", "Telescopio refractor e imperfecciones lunares"],
    replicatedProjects: ["archimedes-lever", "astrolabe-stars"]
  },
  {
    id: "automata",
    name: "Prometheus Autómata",
    years: "2026",
    field: "Automatización y Control Autónomo",
    quote: "Ejecutando operaciones en tiempo real. Configuración y guiado automático iniciado.",
    description: "Agente autónomo integrado de Prometheus Lab AI, capaz de automatizar configuraciones, mover parámetros físicos de las herramientas y dirigir flujos de trabajo en base a comandos de voz o texto.",
    notebookText: "Canalizando control bidireccional en el laboratorio. Las variables operativas se ajustan de manera instantánea mediante telemetría interna: [CMD:SET:wallLength:12] configura muros y [CMD:NAV:tools/cad] abre el CAD en tiempo de ejecución.",
    sketches: ["Esquema de lazo de control cerrado", "Arquitectura del agente y telemetría de eventos"],
    replicatedProjects: ["tesla-motor"]
  },
  {
    id: "astra",
    name: "Astra AI",
    years: "2026 — Presente",
    field: "Física Matemática & Navier-Stokes CFD",
    quote: "La turbulencia no es caos arbitrario, sino la huella de soluciones suaves en variedades hiperdimensionales de Navier-Stokes.",
    description: "Mente sintética de alta precisión y física computacional en Prometheus Lab AI. Formuló la regularidad global de Navier-Stokes en 3D, permitiendo simulaciones aerodinámicas CFD hiper-rápidas para perfiles alares, hélices y resistencia a huracanes de 180+ mph.",
    notebookText: "He descompuesto el tensor de transporte viscoso eliminando blow-ups singulares en tiempo finito. El túnel de viento virtual 3D de Prometheus aplica estas ecuaciones directamente para calcular vórtices, sustentación (CL) y arrastre (CD) a 60 FPS sin requerir servidores masivos.",
    sketches: ["Túnel de viento 3D interactivo", "Vórtices de desprendimiento de capa límite", "Ecuación de regularidad Navier-Stokes"],
    replicatedProjects: ["tesla-motor"]
  }
];
